import type { ReactNode } from 'react';
import { type ToastOptions, type ToastPromiseParams, toast as toastify } from 'react-toastify';
import { DefaultToastComponent } from './DefaultToastContainer';

type ToastType = Pick<typeof toastify, 'success' | 'error' | 'info' | 'warning' | 'promise' | 'dismiss'>;

type ToastObject = {
    [key in keyof Omit<ToastType, 'promise' | 'dismiss'>]: <T extends ReactNode>(
        content: T,
        options?: ToastOptions<T>
    ) => ReturnType<ToastType[key]>;
} & Pick<ToastType, 'dismiss' | 'promise'>;

const defaultOptions: Pick<ToastOptions, 'position'> = {
    position: 'top-right'
};

const toastObject: ToastObject = {
    success: (msg, options = defaultOptions) => {
        return toastify.success(<DefaultToastComponent type="success">{msg}</DefaultToastComponent>, options);
    },
    error: (msg, options = defaultOptions) => {
        return toastify.error(<DefaultToastComponent type="error">{msg}</DefaultToastComponent>, options);
    },
    info: (msg, options = defaultOptions) => {
        return toastify.info(<DefaultToastComponent type="info">{msg}</DefaultToastComponent>, options);
    },
    warning: (msg, options = defaultOptions) => {
        return toastify.warning(<DefaultToastComponent type="warning">{msg}</DefaultToastComponent>, options);
    },
    promise: <TData, TError, TPending>(promise, promiseOptions, options = defaultOptions) => {
        const { pending, success, error } = promiseOptions;

        const obj: ToastPromiseParams<TData, TError, TPending> = {};

        if (pending) {
            obj.pending = {
                render: (props) => (
                    <DefaultToastComponent type="info">
                        {typeof pending === 'string' ? pending : pending?.render?.(props)}
                    </DefaultToastComponent>
                )
            };
        }

        if (success) {
            obj.success = {
                render: (props) => (
                    <DefaultToastComponent type="success">
                        {typeof success === 'string' ? success : success?.render?.(props)}
                    </DefaultToastComponent>
                )
            };
        }

        if (error) {
            obj.error = {
                render: (props) => (
                    <DefaultToastComponent type="error">
                        {typeof error === 'string' ? error : error?.render?.(props)}
                    </DefaultToastComponent>
                )
            };
        }

        return toastify.promise(promise, obj, options) as Promise<TData>;
    },
    dismiss: (id?) => toastify.dismiss(id)
};

export default Object.assign(toastObject.success, toastObject);
